import Nav from './Nav';
import React, { useState, useEffect, useCallback } from 'react';

const TOOL_META = {
  sermon:       { icon: '📖', name: 'Sermon' },
  announcement: { icon: '📢', name: 'Announcement' },
  discussion:   { icon: '👥', name: 'Discussion' },
  devotional:   { icon: '✍️', name: 'Devotional' },
  pastoral:     { icon: '🤝', name: 'Pastoral Care' },
  evangelism:   { icon: '💬', name: 'Evangelism' },
  prayer:       { icon: '🙏', name: 'Prayer' },
  series:       { icon: '🗓️', name: 'Series' },
  biblestudy:   { icon: '📚', name: 'Bible Study' },
  eventpromo:   { icon: '🎉', name: 'Event Promo' },
};

const ROLE_LABELS = { free: '🆓 Free', pro: '⭐ Pro', admin: '🛡️ Admin' };
const ROLE_COLORS = {
  free:  { bg: '#F3F4F6', text: '#6B7280' },
  pro:   { bg: '#FEF3C7', text: '#92400E' },
  admin: { bg: '#DBEAFE', text: '#1E40AF' },
};

function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS.free;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: c.bg, color: c.text }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatCard({ label, value, sub, subColor }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
      <p className="text-3xl font-bold" style={{ color: '#1B3A5C' }}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${subColor || 'text-gray-400'}`}>{sub}</p>}
    </div>
  );
}

export default function AdminDashboard({ token, onBack, userRole, onAuthExpired, onNavigate, onLogout, currentPage }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [roleChangeMap, setRoleChangeMap] = useState({});
  const [actionMsg, setActionMsg] = useState('');

  const fetchStats = useCallback(() => {
    setLoadingStats(true);
    fetch('/api/admin/stats', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.status === 401) { onAuthExpired(); return null; } return r.json(); })
      .then(data => { if (data) setStats(data); setLoadingStats(false); })
      .catch(() => setLoadingStats(false));
  }, [token, onAuthExpired]);

  const fetchUsers = useCallback((s, r) => {
    setLoadingUsers(true);
    const params = new URLSearchParams();
    if (s) params.set('search', s);
    if (r) params.set('role', r);
    fetch(`/api/admin/users?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.status === 401) { onAuthExpired(); return null; } return res.json(); })
      .then(data => { if (data) setUsers(data.users); setLoadingUsers(false); })
      .catch(() => setLoadingUsers(false));
  }, [token, onAuthExpired]);

  useEffect(() => { fetchStats(); fetchUsers('', ''); }, [fetchStats, fetchUsers]);

  const handleSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    fetchUsers(v, roleFilter);
  };

  const handleRoleFilter = (e) => {
    const v = e.target.value;
    setRoleFilter(v);
    fetchUsers(search, v);
  };

  const handleRoleChange = async (userId, newRole) => {
    setRoleChangeMap(m => ({ ...m, [userId]: true }));
    try {
      const res = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
        setActionMsg(`Role updated to ${newRole}.`);
        setTimeout(() => setActionMsg(''), 3000);
        fetchStats();
      }
    } catch {}
    setRoleChangeMap(m => ({ ...m, [userId]: false }));
  };

  const handleDelete = async (userId) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setUsers(prev => prev.filter(u => u.id !== userId));
        setDeleteConfirmId(null);
        setActionMsg('User deleted.');
        setTimeout(() => setActionMsg(''), 3000);
        fetchStats();
      }
    } catch {}
  };

  // Tool usage bar chart data
  const maxToolCount = stats ? Math.max(1, ...Object.values(stats.generations_by_tool || {})) : 1;

  // Redirect if not admin (after all hooks)
  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-500 font-semibold mb-4">Access denied. Admin only.</p>
          <button onClick={onBack} className="text-blue-600 hover:underline text-sm">← Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Nav */}
      <Nav onNavigate={onNavigate} onLogout={onLogout} userRole={userRole} currentPage={currentPage} />

      <main className="flex-1 px-4 py-10 max-w-6xl mx-auto w-full">
        <h2 className="text-2xl font-bold mb-8" style={{ color: '#1B3A5C' }}>Admin Dashboard</h2>

        {/* ── SECTION 1: Stats Cards ── */}
        {loadingStats ? (
          <p className="text-gray-400 text-center py-10">Loading stats...</p>
        ) : stats ? (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            <StatCard
              label="Total Users"
              value={stats.total_users}
              sub={`+${stats.new_users_this_month} this month`}
              subColor="text-green-600"
            />
            <StatCard
              label="Total Generations"
              value={stats.total_generations}
              sub={`${stats.generations_this_month} this month`}
            />
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Free vs Pro</p>
              <p className="text-3xl font-bold mb-2" style={{ color: '#1B3A5C' }}>
                {stats.free_users}<span className="text-lg text-gray-400"> / {stats.pro_users}</span>
              </p>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: stats.total_users > 0
                      ? `${Math.round((stats.pro_users / stats.total_users) * 100)}%`
                      : '0%',
                    backgroundColor: '#C8921A',
                  }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">{stats.free_users} free · {stats.pro_users} pro</p>
            </div>
            <StatCard
              label="Most Used Tool"
              value={TOOL_META[stats.most_used_tool]?.icon || '—'}
              sub={TOOL_META[stats.most_used_tool]?.name || 'None yet'}
            />
            <StatCard
              label="API Cost (Month)"
              value={stats.api_cost_estimate}
              sub="Est. at $0.003/gen"
            />
          </div>
        ) : null}

        {/* ── SECTION 2: Tool Usage ── */}
        {stats && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-8">
            <h3 className="text-base font-semibold mb-5" style={{ color: '#1B3A5C' }}>Tool Usage Breakdown</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(TOOL_META).map(([key, { icon, name }]) => {
                const count = stats.generations_by_tool?.[key] ?? 0;
                const pct = Math.round((count / maxToolCount) * 100);
                return (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-xl w-7 text-center flex-shrink-0">{icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-gray-700">{name}</span>
                        <span className="text-gray-400">{count}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: '#1B3A5C' }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── SECTION 3: User Management ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-base font-semibold mb-5" style={{ color: '#1B3A5C' }}>User Management</h3>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <input
              type="text"
              placeholder="Search by email or church name..."
              value={search}
              onChange={handleSearch}
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
            />
            <select
              value={roleFilter}
              onChange={handleRoleFilter}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-900"
            >
              <option value="">All Roles</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {actionMsg && (
            <p className="text-sm text-green-600 mb-3">{actionMsg}</p>
          )}

          {loadingUsers ? (
            <p className="text-gray-400 text-sm text-center py-8">Loading users...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Church</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Plan</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Joined</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Gens</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-gray-400 text-sm">No users found.</td></tr>
                  )}
                  {users.map(user => (
                    <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-3">
                        <p className="font-medium text-gray-800 truncate max-w-[180px]">{user.email}</p>
                        {user.display_name && <p className="text-xs text-gray-400 truncate">{user.display_name}</p>}
                      </td>
                      <td className="py-3 px-3 text-gray-500 text-xs max-w-[140px] truncate">
                        {user.church_name || '—'}
                      </td>
                      <td className="py-3 px-3">
                        <RoleBadge role={user.role} />
                      </td>
                      <td className="py-3 px-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(user.member_since)}</td>
                      <td className="py-3 px-3 text-gray-600 font-medium">{user.generation_count}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={user.role}
                            disabled={roleChangeMap[user.id]}
                            onChange={e => handleRoleChange(user.id, e.target.value)}
                            className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-900 disabled:opacity-50"
                          >
                            <option value="free">Free</option>
                            <option value="pro">Pro</option>
                            <option value="admin">Admin</option>
                          </select>

                          {deleteConfirmId === user.id ? (
                            <span className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(user.id)}
                                className="text-xs px-2 py-1 rounded border border-red-400 bg-red-50 text-red-700 font-medium"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="text-xs text-gray-400 hover:text-gray-600 px-1"
                              >
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(user.id)}
                              className="text-xs px-2 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
